"use client";

import { isHeicImageBytes } from "@/utils/heic-image";
import type { ImageProps } from "next/image";
import NextImage from "next/image";
import { useEffect, useState } from "react";
import cn from "twsx";
export function HeicImage({
  src,
  alt,
  onConversionError,
  ...props
}: ImageProps & {
  src: string;
  onConversionError?: () => void;
}) {
  const [preview, setPreview] = useState<{
    source: string;
    url: string | null;
  }>();
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | undefined;
    async function prepare() {
      try {
        const response = await fetch(src, {
          signal: controller.signal
        });
        if (!response.ok) throw new Error("Image could not be loaded");
        if (Number(response.headers.get("content-length")) > 25 * 1024 * 1024) throw new Error("Image exceeds preview limits");
        const blob = await response.blob();
        if (controller.signal.aborted) return;
        if (!isHeicImageBytes(new Uint8Array(await blob.slice(0, 64).arrayBuffer()))) throw new Error("Image is not HEIC");
        const {
          convertHeicPreview
        } = await import("@/utils/heic-preview.client");
        const image = await convertHeicPreview(blob);
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(image);
        setPreview({
          source: src,
          url: objectUrl
        });
      } catch {
        if (!controller.signal.aborted) {
          setPreview({
            source: src,
            url: null
          });
          onConversionError?.();
        }
      }
    }
    void prepare();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, onConversionError]);
  const current = preview?.source === src ? preview : undefined;
  if (current?.url) return <NextImage {...props} alt={alt} src={current.url} unoptimized />;
  return <span className={cn("flex items-center justify-center bg-bg-secondary text-center text-base text-text-tertiary", props.fill && "absolute inset-0", props.className)} role="status">
      {current ? <a href={src} target="_blank" rel="noreferrer" className="text-text-link hover:underline">Preview unavailable. Download original</a> : "Loading image…"}
    </span>;
}